import type { InsightsSavedView, SavedViewFilters } from '#services/api/saved-views.ts';

import * as React from 'react';
import { useSearchParams } from 'react-router';

import {
  applyFiltersToSearchParams,
  getParsedSearchParams,
  serializeFilters,
} from '../Home.utils.ts';

type SelectedViewState = { mode: 'route' } | { mode: 'none' } | { mode: 'view'; viewId: number };

const FiltersContext = React.createContext<{
  /** Applies filter overrides to the current URL state. */
  onChange: (overrides: SavedViewFilters) => void;
  /** Remembers the last saved view involved in the current session. */
  lastActiveViewId: number | undefined;
  /** Tracks whether selection comes from the route, a local view, or neither. */
  selectedViewState: SelectedViewState;
  /** Marks a saved view as the current local selection. */
  selectView: (viewId: number) => void;
  /** Restores route-driven saved view state after closing a drilldown. */
  restoreRouteStateFromDrilldown: () => void;
} | null>(null);

export function FiltersProvider(props: {
  activeView: InsightsSavedView | undefined;
  tabType: 'scheduling' | 'forms';
  children: React.ReactNode;
}) {
  const [, setSearchParams] = useSearchParams();
  const [lastActiveViewId, setLastActiveViewId] = React.useState(props.activeView?.id);
  const [selectedViewState, setSelectedViewState] = React.useState<SelectedViewState>({
    mode: 'route',
  });

  const value = React.useMemo(
    () => ({
      onChange: (overrides: SavedViewFilters) => {
        setSearchParams((prev) => {
          const base =
            props.activeView?.filters ??
            serializeFilters({
              parsed: getParsedSearchParams(prev),
              tabType: props.tabType,
            });
          const normalizedBase = normalizeFilters(base);
          const normalizedFilters = normalizeFilters({ ...base, ...overrides });

          if (areFiltersEqual(normalizedFilters, normalizedBase)) {
            return prev;
          }

          const next = applyFiltersToSearchParams(normalizedFilters, prev);

          if (props.activeView) {
            setLastActiveViewId(props.activeView.id);
            setSelectedViewState({ mode: 'none' });
            next.delete('view');
            next.set('tab', props.activeView.tabType);
          }

          return next;
        });
      },
      lastActiveViewId,
      selectedViewState,
      selectView: (viewId: number) => {
        setLastActiveViewId(viewId);
        setSelectedViewState({ mode: 'view', viewId });
      },
      restoreRouteStateFromDrilldown: () => {
        if (!props.activeView) {
          setLastActiveViewId(undefined);
          setSelectedViewState({ mode: 'none' });
          return;
        }

        setLastActiveViewId(props.activeView.id);
        setSelectedViewState({ mode: 'route' });
      },
    }),
    [props.activeView, props.tabType, setSearchParams, lastActiveViewId, selectedViewState]
  );

  return <FiltersContext value={value}>{props.children}</FiltersContext>;
}

// This file isn't touched that often so the rule is disabled to avoid having to create yet another
// file just for this function.
// oxlint-disable-next-line react/only-export-components
export function useFilters() {
  const value = React.use(FiltersContext);
  if (!value) throw new Error('`useFilters` must be called within `FiltersProvider`');
  return value;
}

function normalizeFilters(filters: SavedViewFilters): SavedViewFilters {
  const normalizedEntries = Object.entries(filters).flatMap(([key, value]) => {
    // By using `Object.entries` in combination with the `SavedViewFilters` type from the schema
    // eslint can't "see" that an explicit value of `undefined` is valid.
    // oxlint-disable-next-line typescript/no-unnecessary-condition
    if (value === undefined) {
      return [];
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return [];
      }

      return [[key, [...value].sort()]];
    }

    return [[key, value]];
  });

  return Object.fromEntries(normalizedEntries) as SavedViewFilters;
}

function areFiltersEqual(left: SavedViewFilters, right: SavedViewFilters) {
  return JSON.stringify(left) === JSON.stringify(right);
}
